'use client'

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import {
  MAX_VIDEO_SLIDE_DURATION_MS,
  NO_VIDEO_SLIDE_DURATION_MS,
  VIDEO_CROSSFADE_MS,
  VIDEO_PLAY_ICON_FADE_MS,
  VIDEO_READY_TIMEOUT_MS,
} from '../constants'
import type { HeroCarouselItem } from '../types'

export interface YouTubePlayerHandle {
  playVideo(): void
  pauseVideo(): void
  destroy(): void
}

export type CreateYouTubePlayer = (
  element: HTMLElement,
  options: { videoId: string; onReady: () => void; onEnded: () => void },
) => YouTubePlayerHandle

/** Envuelve `new window.YT.Player(...)` — inyectable en tests para controlar
 * `onReady`/`onEnded` sin depender de la red ni de un iframe real.
 *
 * IMPORTANTE: la API de YouTube no monta el iframe DENTRO del elemento que se
 * le pasa — lo SUSTITUYE (`element.replaceWith(iframe)`), perdiendo cualquier
 * clase/estilo/posicionamiento que ese elemento tuviera (y, si `element` es el
 * mismo nodo que React sigue vía `ref`, deja esa ref apuntando a un nodo ya
 * desprendido del árbol, imposible de reutilizar en una recreación posterior
 * — visto en vivo: el iframe aparecía en la esquina superior izquierda a
 * 640×360, sin `position:absolute`/`inset:0`, porque la clase `.videoSlot` la
 * llevaba el `<div>` que YouTube acababa de reemplazar). Por eso NUNCA se le
 * pasa a `YT.Player` el contenedor estable que React controla (`element`):
 * se crea aquí un `<div>` "sacrificable" nuevo en cada llamada, se cuelga
 * dentro de `element` (que él sí permanece siempre en el árbol, con sus
 * estilos de posición/opacidad intactos) y ESE hijo desechable es lo que se
 * ofrece a YouTube para sustituir. */
/** Sobrescribe, inline, el `width:100%;height:100%` que la propia API de
 * YouTube pone en el `<iframe>` que genera (heredado del `mountPoint`
 * sacrificable de abajo) — un estilo inline siempre gana a cualquier regla
 * de `HeroCarousel.module.scss`, así que el "cover" 16:9 de ahí nunca llega
 * a aplicarse mientras esto quede sin pisar. Se reescribe aquí, también
 * inline (misma especificidad, pero última escritura sobre el mismo
 * atributo), con el mismo criterio: sobredimensionar manteniendo la
 * proporción 16:9 y centrar, para que `overflow: hidden` de `.videoSlot`
 * recorte el excedente. `cqw`/`cqh` (en vez de `%`) porque el ancho necesita
 * depender también de la altura del contenedor —y viceversa— para cubrirlo
 * sea cual sea su proporción (requiere `container-type: size` en
 * `.videoSlot`, ya presente en el módulo). */
function applyCoverIframeStyle(container: HTMLElement): void {
  const iframe = container.querySelector('iframe')
  if (!iframe) return
  iframe.style.position = 'absolute'
  iframe.style.top = '50%'
  iframe.style.left = '50%'
  iframe.style.width = '110cqw' // 100cqw × 1.1 de margen
  iframe.style.height = '61.875cqw' // 56.25cqw (100cqw · 9/16) × 1.1
  iframe.style.minWidth = '195.56cqh' // 177.78cqh (100cqh · 16/9) × 1.1
  iframe.style.minHeight = '110cqh'
  iframe.style.transform = 'translate(-50%, -50%)'
  iframe.style.border = '0'
}

function defaultCreatePlayer(
  element: HTMLElement,
  { videoId, onReady, onEnded }: { videoId: string; onReady: () => void; onEnded: () => void },
): YouTubePlayerHandle {
  const YT = window.YT
  if (!YT) return { playVideo() {}, pauseVideo() {}, destroy() {} }

  const mountPoint = document.createElement('div')
  mountPoint.style.width = '100%'
  mountPoint.style.height = '100%'
  element.appendChild(mountPoint)

  const player = new YT.Player(mountPoint, {
    videoId,
    // `mute: 1` en `playerVars` + `player.mute()` en `onReady` (belt-and-braces):
    // el autoplay muted es lo único compatible con las políticas de los
    // navegadores (FR-015). El resto suprime el chrome interactivo de
    // YouTube (título, tarjetas de vídeos relacionados, teclado, pantalla
    // completa) — es un fondo decorativo, no un reproductor con el que se
    // deba poder interactuar (además, `.videoSlot` lleva `pointer-events:
    // none` en CSS, así que ningún hover/click llega nunca al iframe).
    playerVars: {
      autoplay: 0,
      controls: 0,
      mute: 1,
      playsinline: 1,
      modestbranding: 1,
      rel: 0,
      fs: 0,
      disablekb: 1,
      iv_load_policy: 3,
      cc_load_policy: 0,
    },
    events: {
      onReady: () => {
        player.mute()
        applyCoverIframeStyle(element)
        onReady()
      },
      onStateChange: (event) => {
        if (event.data === YT.PlayerState.ENDED) onEnded()
      },
    },
  })

  return {
    playVideo: () => player.playVideo(),
    pauseVideo: () => player.pauseVideo(),
    destroy: () => {
      player.destroy()
      mountPoint.remove() // defensivo: por si `destroy()` no limpia el nodo
    },
  }
}

/** Dos contenedores DOM físicos y estables — un player de YouTube, una vez
 * creado dentro de uno de ellos, nunca "se mueve" al otro. Promover una
 * precarga a slide activa consiste en intercambiar cuál de los dos
 * contenedores se muestra (`visibleSlot`), no en mover el iframe. */
type SlotKey = 'A' | 'B'

function otherSlotKey(key: SlotKey): SlotKey {
  return key === 'A' ? 'B' : 'A'
}

interface PlayerSlot {
  index: number
  handle: YouTubePlayerHandle
  ready: boolean
  /** Una vez reproducido, no se reutiliza como precarga aunque su índice
   * volviera a coincidir con el siguiente (carrusel de 2 items) — se libera
   * y se crea uno nuevo, para no depender de a qué posición queda el
   * reproductor tras terminar (FR-022). */
  hasPlayed: boolean
}

interface UseHeroCarouselPlaybackOptions {
  /** Ya normalizados, ordenados y validados (≥2) — ver `normalizeHeroCarouselItems`. */
  items: HeroCarouselItem[]
  /** `true` cuando `window.YT.Player` ya está disponible (`useYouTubeIframeApi`). */
  apiReady: boolean
  /** Inyectable en tests; por defecto envuelve `window.YT.Player`. */
  createPlayer?: CreateYouTubePlayer
  /** `false` mientras el carrusel está fuera del viewport — detiene el avance
   * y el vídeo igual que la pausa manual, pero sin tocar `isPaused`: al volver
   * a la vista se reanuda solo, salvo que el usuario lo hubiera pausado. */
  isInView?: boolean
}

export interface UseHeroCarouselPlaybackResult {
  activeIndex: number
  /** `true` mientras la slide activa reproduce su vídeo — la capa visual
   * muestra la imagen panorámica de fondo cuando es `false` (fallback de
   * carga inicial o de precarga no lista, FR-018/FR-021). */
  isVideoPlaying: boolean
  /** Cuál de los dos contenedores físicos debe mostrarse ahora — el otro se
   * mantiene oculto (puede estar precargando el vídeo siguiente, o
   * desvaneciéndose todavía tras dejar de ser el activo). */
  visibleSlot: SlotKey
  slotARef: (element: HTMLDivElement | null) => void
  slotBRef: (element: HTMLDivElement | null) => void
  /** Navegación manual — cíclica. */
  goTo: (index: number) => void
  /** `true` mientras el usuario ha pausado, con el control de pausa, el
   * avance automático (y, si la slide activa tiene vídeo, también su
   * reproducción). Es un estado del carrusel entero: se mantiene al navegar
   * manualmente a otra slide, que tampoco arranca su vídeo hasta reanudar. */
  isPaused: boolean
  /** Alterna `isPaused`. El efecto sobre el reproductor activo
   * (`playVideo`/`pauseVideo`) lo aplica el efecto que observa la detención
   * efectiva (pausa manual o fuera del viewport). */
  togglePause: () => void
  /** `true` mientras la slide activa tiene un vídeo intentando reproducirse
   * — desde el instante en que se activa (aunque su reproductor aún no esté
   * listo) hasta que cambia de slide o renuncia al vídeo por plazo
   * (`VIDEO_READY_TIMEOUT_MS`). El control de pausa se muestra siempre
   * (también en slides sin vídeo, para poder parar el avance a la
   * siguiente); esto solo distingue si además hay un vídeo que pausar, para
   * matizar su etiqueta accesible. */
  hasVideoSlide: boolean
}

/**
 * Orquesta el avance de slides del HeroCarousel: temporizador de 3s para
 * slides sin vídeo (FR-013), avance por `YT.PlayerState.ENDED` para slides
 * con vídeo (FR-014), y precarga de un único vídeo siguiente en segundo
 * plano mientras el actual reproduce (FR-016 a FR-020). Nunca bloquea el
 * cambio de slide esperando a YouTube: si la precarga no está lista, cambia
 * igualmente (fallback a imagen) y arranca el vídeo en cuanto esté listo
 * solo si esa slide sigue siendo la activa (FR-018/FR-019/FR-020). El player
 * que deja de ser el activo se libera con el mismo fundido CSS que el fondo
 * (`VIDEO_CROSSFADE_MS`), no de golpe.
 */
export function useHeroCarouselPlayback({
  items,
  apiReady,
  createPlayer = defaultCreatePlayer,
  isInView = true,
}: UseHeroCarouselPlaybackOptions): UseHeroCarouselPlaybackResult {
  const count = items.length
  const [activeIndex, setActiveIndex] = useState(0)
  const [activeSlotKey, setActiveSlotKey] = useState<SlotKey>('A')
  /** Slide+slot cuyo vídeo ya se reveló (tras `VIDEO_PLAY_ICON_FADE_MS`).
   * `isVideoPlaying` se deriva de esto: cualquier cambio de slide o de slot
   * deja de coincidir y vuelve solo a `false`, sin resetearlo desde un efecto. */
  const [revealed, setRevealed] = useState<{ index: number; key: SlotKey } | null>(null)
  const isVideoPlaying = revealed?.index === activeIndex && revealed.key === activeSlotKey
  /** Índice de la slide activa que renunció a su vídeo por no llegar a estar
   * listo dentro de `VIDEO_READY_TIMEOUT_MS` — mientras coincida con
   * `activeIndex`, esa pasada se trata como una slide sin vídeo. Vuelve a
   * `null` en cuanto `activeIndex` cambia, para que un ciclo posterior por la
   * misma slide tenga de nuevo su plazo completo. */
  const [videoGaveUpIndex, setVideoGaveUpIndex] = useState<number | null>(null)
  const [isPaused, setIsPaused] = useState(false)
  /** Detención efectiva: pausa manual del usuario o carrusel fuera de vista. */
  const isHalted = isPaused || !isInView

  const activeIndexRef = useRef(activeIndex)
  const activeSlotKeyRef = useRef<SlotKey>(activeSlotKey)
  const isHaltedRef = useRef(isHalted)
  // Los refs "espejo" se actualizan tras cada render (no durante él): el layout
  // effect corre antes que cualquier efecto pasivo y que cualquier callback
  // asíncrono (timers, `onReady`/`onEnded`), así que siempre leen el valor vigente.
  useLayoutEffect(() => {
    activeIndexRef.current = activeIndex
    activeSlotKeyRef.current = activeSlotKey
    isHaltedRef.current = isHalted
  })

  const slotsRef = useRef<Record<SlotKey, PlayerSlot | null>>({ A: null, B: null })
  const containersRef = useRef<Record<SlotKey, HTMLDivElement | null>>({ A: null, B: null })

  const slotARef = useCallback((element: HTMLDivElement | null) => {
    containersRef.current.A = element
  }, [])
  const slotBRef = useCallback((element: HTMLDivElement | null) => {
    containersRef.current.B = element
  }, [])

  const goTo = useCallback(
    (index: number) => {
      if (count === 0) return
      // `isPaused` NO se toca: la pausa del usuario persiste al cambiar de
      // slide. La nueva slide recupera su plazo completo de
      // `VIDEO_READY_TIMEOUT_MS` aunque sea la misma que ya se dio por
      // vencida en un ciclo anterior (carrusel cíclico).
      setVideoGaveUpIndex(null)
      // Evita que un vídeo revelado en un ciclo anterior de la misma slide
      // (carrusel de 2 items) se dé por reproduciéndose al volver a ella.
      setRevealed(null)
      setActiveIndex(((index % count) + count) % count)
    },
    [count],
  )

  const advance = useCallback(() => {
    if (count === 0) return
    goTo(activeIndexRef.current + 1)
  }, [goTo, count])

  /** Ver `VIDEO_PLAY_ICON_FADE_MS` — retrasa solo la revelación visual
   * (`isVideoPlaying: true`), nunca la llamada a `playVideo()`. Misma guarda
   * `key`+`index` que `onReady`/`onEnded`: si para cuando venza el margen la
   * slide activa ya es otra, no revela nada (evita un "flash" de un vídeo
   * que el usuario ya abandonó). */
  const revealVideoAfterIconFade = useCallback((key: SlotKey, index: number) => {
    setTimeout(() => {
      if (activeIndexRef.current === index && activeSlotKeyRef.current === key) {
        setRevealed({ index, key })
      }
    }, VIDEO_PLAY_ICON_FADE_MS)
  }, [])

  /** Libera el slot lógicamente de inmediato (deja de contar para la
   * orquestación) pero retrasa la destrucción real del player
   * `VIDEO_CROSSFADE_MS` — el tiempo que tarda en completarse el fundido CSS
   * de `.videoSlot` — para que el vídeo se desvanezca visualmente en vez de
   * desaparecer en el mismo instante en que cambia la slide activa. */
  const retireSlot = useCallback((key: SlotKey) => {
    const slot = slotsRef.current[key]
    if (!slot) return
    slotsRef.current[key] = null
    setTimeout(() => slot.handle.destroy(), VIDEO_CROSSFADE_MS)
  }, [])

  /** `onReady`/`onEnded` comparten la misma guarda: solo actúan si, para
   * cuando se disparan, `key`+`index` siguen siendo los de la slide activa.
   * Cubre tanto "esta slide activa aún cargaba" (arranca en cuanto puede,
   * FR-019) como "esto era una precarga que el usuario ya abandonó" (nunca
   * arranca, FR-020) con la misma regla. */
  const createSlot = useCallback(
    (key: SlotKey, index: number, container: HTMLElement, videoId: string): PlayerSlot => {
      const slot: PlayerSlot = {
        index,
        ready: false,
        hasPlayed: false,
        handle: null as unknown as YouTubePlayerHandle,
      }
      slot.handle = createPlayer(container, {
        videoId,
        onReady: () => {
          slot.ready = true
          // Si el carrusel está detenido (pausa manual o fuera de vista)
          // cuando el reproductor llega a estar listo, no arranca solo — se
          // queda preparado hasta que el efecto de detención lo reanude.
          if (
            activeIndexRef.current === index &&
            activeSlotKeyRef.current === key &&
            !isHaltedRef.current
          ) {
            slot.handle.playVideo()
            slot.hasPlayed = true
            revealVideoAfterIconFade(key, index)
          }
        },
        onEnded: () => {
          if (activeIndexRef.current === index && activeSlotKeyRef.current === key) advance()
        },
      })
      return slot
    },
    [createPlayer, advance, revealVideoAfterIconFade],
  )

  // ── Slide activa: temporizador (sin vídeo) o reproducción (con vídeo). ──
  useEffect(() => {
    if (count === 0) return
    const activeItem = items[activeIndex]
    if (!activeItem) return

    const slots = slotsRef.current

    // Slide sin vídeo — o CON vídeo que renunció por no llegar a estar listo
    // dentro de `VIDEO_READY_TIMEOUT_MS` (ver el efecto de plazo, abajo): no
    // hay nada que enseñar en los slots. Se retira (con fundido) cualquier
    // contenido que quedara visible de la slide anterior. No se toca
    // `activeSlotKey`: sin vídeo no hay ninguna noción de "cuál de los dos
    // slots" aplica. El avance por temporizador vive en un efecto aparte (ver
    // más abajo), para poder depender de `isHalted` sin arrastrar el resto de
    // esta orquestación de vídeo.
    if (!activeItem.videoId || videoGaveUpIndex === activeIndex) {
      retireSlot('A')
      retireSlot('B')
      return
    }

    // A partir de aquí la slide activa SÍ tiene vídeo.
    if (slots[activeSlotKey]?.index !== activeIndex) {
      // Slide nueva: el póster/imagen panorámica es lo visible de inmediato,
      // aunque el vídeo ya estuviera precargado — `isVideoPlaying` (derivado
      // de `revealed`) es `false` hasta que venza `VIDEO_PLAY_ICON_FADE_MS`,
      // que oculta el icono de YouTube.
      const preloadKey = otherSlotKey(activeSlotKey)
      const preloaded = slots[preloadKey]

      if (preloaded?.index === activeIndex && !preloaded.hasPlayed) {
        // Ya estaba precargada — solo cambia cuál de los dos contenedores se
        // muestra (cross-fade CSS); el que deja de verse se retira igual.
        retireSlot(activeSlotKey)
        setActiveSlotKey(preloadKey)
        return
      }

      if (!apiReady) {
        // Aún no se puede preparar el vídeo nuevo (script no cargado todavía)
        // — no se cambia de slot ni se retira nada para no oscilar sin fin;
        // el efecto se reintenta solo cuando `apiReady` cambie.
        return
      }

      const container = containersRef.current[preloadKey]
      if (!container) return

      retireSlot(activeSlotKey)
      // El slot que hubiera en `preloadKey` era, como mucho, una precarga —
      // `visibleSlot` nunca apunta a él, así que nunca llegó a mostrarse — y
      // puede liberarse al instante (a diferencia de `retireSlot`, arriba,
      // reservado para lo que sí se veía y necesita completar su fundido).
      const stalePreload = slots[preloadKey]
      if (stalePreload) stalePreload.handle.destroy()
      slots[preloadKey] = createSlot(preloadKey, activeIndex, container, activeItem.videoId)
      setActiveSlotKey(preloadKey)
      return
    }

    if (!apiReady) return

    const slot = slots[activeSlotKey]
    // Detenido (el efecto de detención arrancará al reanudar) o aún
    // inicializando (`onReady` completará el arranque si sigue activa): no se
    // arranca nada y la capa visual sigue mostrando la imagen de fondo
    // (FR-021).
    if (slot?.ready && !isHaltedRef.current) {
      slot.handle.playVideo()
      slot.hasPlayed = true
      revealVideoAfterIconFade(activeSlotKey, activeIndex)
    }
  }, [
    activeIndex,
    activeSlotKey,
    apiReady,
    items,
    count,
    videoGaveUpIndex,
    createSlot,
    retireSlot,
    revealVideoAfterIconFade,
  ])

  // ── Plazo de `VIDEO_READY_TIMEOUT_MS` para que el vídeo de la slide activa
  // esté listo — si no lo consigue a tiempo, esta pasada renuncia al vídeo
  // (`videoGaveUpIndex`) y cae al mismo camino que una slide sin vídeo
  // (imagen + `NO_VIDEO_SLIDE_DURATION_MS`), en vez de esperar hasta el tope
  // de `MAX_VIDEO_SLIDE_DURATION_MS` sin mostrar nada. Efecto aparte, con solo
  // `activeIndex` como disparador real: se arma una única vez por activación
  // de la slide, no en cada cambio de `activeSlotKey` durante esa misma
  // activación.
  useEffect(() => {
    if (count === 0) return
    const activeItem = items[activeIndex]
    if (!activeItem?.videoId || videoGaveUpIndex === activeIndex) return

    const timer = setTimeout(() => {
      if (activeIndexRef.current !== activeIndex) return
      const key = activeSlotKeyRef.current
      const slot = slotsRef.current[key]
      if (slot?.ready) return // llegó a tiempo — nada que hacer

      if (slot) {
        slotsRef.current[key] = null
        slot.handle.destroy()
      }
      setVideoGaveUpIndex(activeIndex)
    }, VIDEO_READY_TIMEOUT_MS)

    return () => clearTimeout(timer)
  }, [activeIndex, items, count, videoGaveUpIndex])

  // ── Tope de duración de una slide con vídeo — avanza a los 15s aunque el
  // vídeo no haya terminado (algunos del CMS pueden ser muy largos). Efecto
  // aparte y solo con `activeIndex` como dependencia real: si el vídeo
  // termina antes (`onEnded` → `advance`) o el usuario navega manualmente,
  // `activeIndex` cambia y la limpieza cancela este timer sin que llegue a
  // disparar. Con el carrusel detenido (pausa o fuera de vista) no se
  // programa nada — al reanudar se arma un tope fresco de 15s completos, sin
  // descontar lo ya transcurrido antes de detenerse.
  useEffect(() => {
    if (count === 0 || isHalted) return
    const activeItem = items[activeIndex]
    if (!activeItem?.videoId || videoGaveUpIndex === activeIndex) return

    const timer = setTimeout(advance, MAX_VIDEO_SLIDE_DURATION_MS)
    return () => clearTimeout(timer)
  }, [activeIndex, items, count, isHalted, videoGaveUpIndex, advance])

  // ── Avance por temporizador de una slide SIN vídeo — o que renunció al
  // suyo (`videoGaveUpIndex`). Efecto aparte, simétrico al de
  // `MAX_VIDEO_SLIDE_DURATION_MS` de arriba: depende de `isHalted` sin
  // arrastrar el resto de la orquestación de vídeo (evitaría, si viviera en
  // el efecto principal, repetir `playVideo()` sobre un vídeo ya en marcha
  // cada vez que se reanuda). Detenido no se programa nada — al reanudar se
  // arma un tope fresco de `NO_VIDEO_SLIDE_DURATION_MS` completos.
  useEffect(() => {
    if (count === 0 || isHalted) return
    const activeItem = items[activeIndex]
    const hasVideo = Boolean(activeItem?.videoId) && videoGaveUpIndex !== activeIndex
    if (hasVideo) return

    const timer = setTimeout(advance, NO_VIDEO_SLIDE_DURATION_MS)
    return () => clearTimeout(timer)
  }, [activeIndex, items, count, isHalted, videoGaveUpIndex, advance])

  // ── Precarga de la siguiente slide (una sola, en segundo plano). Detenido
  // (pausa o fuera de vista) no se precarga nada: no tiene sentido descargar
  // un vídeo que no va a llegar mientras siga así — al reanudar, este efecto
  // vuelve a correr y la prepara. ──
  useEffect(() => {
    if (count < 2 || !apiReady || isHalted) return
    const nextIndex = (activeIndex + 1) % count
    const nextItem = items[nextIndex]
    if (!nextItem) return

    const slots = slotsRef.current
    const preloadKey = otherSlotKey(activeSlotKey)
    const existing = slots[preloadKey]

    // `existing` puede ser en realidad el slot que el otro efecto (slide
    // activa) acaba de promover a activo en este mismo render: su
    // `setActiveSlotKey` todavía no se ha aplicado, así que aquí seguimos
    // viendo el `activeSlotKey` antiguo y calculamos `preloadKey` apuntando
    // al slot que ya sirve a la slide activa. Nunca se destruye eso — si no,
    // el otro efecto lo recrea y vuelve a promoverlo, y este lo destruye de
    // nuevo: bucle infinito de `setActiveSlotKey` (visto en producción).
    // `existing` puede ser en realidad el slot que el otro efecto (slide
    // activa) acaba de promover a activo en este mismo render: su
    // `setActiveSlotKey` todavía no se ha aplicado, así que aquí seguimos
    // viendo el `activeSlotKey` antiguo y calculamos `preloadKey` apuntando
    // al slot que ya sirve a la slide activa. Nunca se destruye eso — si no,
    // el otro efecto lo recrea y vuelve a promoverlo, y este lo destruye de
    // nuevo: bucle infinito de `setActiveSlotKey` (visto en producción, y
    // reproducible quitando este guard — ver historial de este archivo).
    if (existing?.index === activeIndex) return

    const reusable = existing?.index === nextIndex && !existing.hasPlayed

    // Esta precarga nunca ha sido visible, así que puede liberarse al
    // instante si queda obsoleta (a diferencia de `retireSlot`, que difiere
    // la destrucción del slot activo para no cortar su fundido).
    if (existing && !reusable) {
      existing.handle.destroy()
      slots[preloadKey] = null
    }

    const activeAlreadyCoversNext = slots[activeSlotKey]?.index === nextIndex
    if (!nextItem.videoId || slots[preloadKey] || activeAlreadyCoversNext) return

    const container = containersRef.current[preloadKey]
    if (!container) return

    slots[preloadKey] = createSlot(preloadKey, nextIndex, container, nextItem.videoId)
  }, [activeIndex, activeSlotKey, apiReady, items, count, isHalted, createSlot])

  // ── Liberar todo al desmontar. ──
  useEffect(() => {
    // Objeto estable que solo se muta (nunca se reasigna `slotsRef.current`),
    // así que copiarlo aquí equivale a leerlo en el cleanup.
    const slots = slotsRef.current
    return () => {
      slots.A?.handle.destroy()
      slots.B?.handle.destroy()
      slots.A = null
      slots.B = null
    }
  }, [])

  // ── Detención (pausa manual o fuera de vista): aplica
  // `pauseVideo`/`playVideo` sobre el reproductor activo solo en las
  // transiciones de `isHalted`, nunca en el montaje. Si el vídeo llegó a
  // estar listo estando detenido (`onReady` no lo arrancó, `!hasPlayed`), al
  // reanudar hay que completar ese arranque igual que lo haría `onReady`.
  const wasHaltedRef = useRef(isHalted)
  useEffect(() => {
    if (wasHaltedRef.current === isHalted) return
    wasHaltedRef.current = isHalted

    const key = activeSlotKeyRef.current
    const slot = slotsRef.current[key]
    if (!slot?.ready || slot.index !== activeIndexRef.current) return

    if (isHalted) {
      slot.handle.pauseVideo()
      return
    }
    slot.handle.playVideo()
    if (!slot.hasPlayed) {
      slot.hasPlayed = true
      revealVideoAfterIconFade(key, slot.index)
    }
  }, [isHalted, revealVideoAfterIconFade])

  const togglePause = useCallback(() => setIsPaused((prev) => !prev), [])

  const hasVideoSlide = Boolean(items[activeIndex]?.videoId) && videoGaveUpIndex !== activeIndex

  return {
    activeIndex,
    isVideoPlaying,
    visibleSlot: activeSlotKey,
    slotARef,
    slotBRef,
    goTo,
    isPaused,
    togglePause,
    hasVideoSlide,
  }
}
